import { Outlet, useParams } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import type { ComponentProps } from "react";
import { useMemo } from "react";
import { I18nextProvider } from "react-i18next";
import type { z } from "zod";

import { TanstackQueryProvider } from "./tanstack-query";
import { ThirdParty } from "./third-party";

import { AuthResolver } from "~/entities/auth";
import { DiContainerContext, initDiContainer } from "~/entities/di";
import type { Config } from "~/shared/config";
import { ConfigContext } from "~/shared/config";
import { DEFAULT_LANGUAGE_CODE, i18nInstance } from "~/shared/i18n";
import { Toaster } from "~/shared/ui/sonner";

type Props<
  UserRoleKey extends z.KeySchema,
  UserRoleValue extends z.ZodType<string, z.ZodTypeDef>,
> = {
  config: Config<UserRoleKey, UserRoleValue>;
};

function App<
  UserRoleKey extends z.KeySchema,
  UserRoleValue extends z.ZodType<string, z.ZodTypeDef>,
>({ config }: Props<UserRoleKey, UserRoleValue>) {
  const { locale = DEFAULT_LANGUAGE_CODE } = useParams({ from: "/{-$locale}" });

  const i18n = useMemo(
    () =>
      i18nInstance.cloneInstance({
        lng: locale,
      }),
    [locale],
  );

  const diContainer = initDiContainer({
    config,
    getLocale: () => locale,
  });

  return (
    <ConfigContext.Provider
      value={config as ComponentProps<typeof ConfigContext.Provider>["value"]}
    >
      <DiContainerContext.Provider value={diContainer}>
        <Toaster />
        <TanstackQueryProvider>
          <I18nextProvider i18n={i18n}>
            <ThirdParty />
            <AuthResolver authService={diContainer.authService} eventBus={diContainer.eventBus} />
            <Outlet />
          </I18nextProvider>
          <TanStackRouterDevtools />
        </TanstackQueryProvider>
      </DiContainerContext.Provider>
    </ConfigContext.Provider>
  );
}

export { App };
